import { useDynamicConfig } from '@statsig/react-bindings';

import { DynamicConfigs, FeatureGates } from 'modules/common/constants/featuregates';
import { useMember } from 'modules/common/queries/member';
import useStatsigGate from 'modules/common/useStatsigGate';
import { hasOptedIntoRetroS } from 'modules/common/utils/retroSElection';

/**
 * Whether the verified, coverage-driven statement path is on for this member.
 *
 * Two switches, and they are not symmetrical:
 *  - `accounting_setup_statement_verification` is the feature's kill switch. Off, nobody sees
 *    the verified experience, whatever the config says.
 *  - `retro_s_only` in `bk_statement_extraction_config` is an *audience filter*, not a second
 *    on/off. False (or absent) leaves the gate as the sole determiner; true narrows the rollout
 *    to members who have opted into retro-S, which the presence of
 *    `business.preferred_s_election_date` records.
 *
 * So the config can only ever take the feature away from someone the gate let in — it can
 * never let someone in on its own.
 *
 * A member who has not loaded yet reads as out of the audience, not in it. Gating on a member
 * property means the property is unknowable for the first frames, and the safe reading of
 * unknown is the experience that already exists.
 *
 * Resolved in one hook rather than at each call site so the render fork (`DocumentUploadList`)
 * and the Continue gate (`useStatementReadiness`) can never disagree — a member shown the legacy
 * rows while Continue waited on coverage would be stuck with nothing on screen explaining why.
 *
 * `ignoreAudience` drops the `retro_s_only` filter only, for a surface whose sole upload is the
 * verified one. The gate still applies, so turning it off still takes the feature away there.
 */
export default function useStatementVerificationEnabled({ ignoreAudience = false } = {}): boolean {
    const isGateOn = useStatsigGate(FeatureGates.AccountingSetupStatementVerification);
    const extractionConfig = useDynamicConfig(DynamicConfigs.BkStatementExtractionConfig);
    const { data: member } = useMember();

    if (!isGateOn) {
        return false;
    }

    // `get` hands back the fallback when the key is absent *or* holds a non-boolean. Defaulting
    // false means a config that predates this key doesn't quietly narrow an existing rollout.
    if (ignoreAudience || !extractionConfig.get('retro_s_only', false)) {
        return true;
    }

    return hasOptedIntoRetroS(member?.business);
}
